"use strict";

/* Upstream motion layer.
 *
 * Sits on top of assets/motion.js (the River Cut current field, hero entrance
 * and count-ups) and adds the qualities that make a site feel expensive:
 * inertial scrolling, magnetic controls, and reveals that are timed to the way
 * a section actually enters the viewport.
 *
 * Deliberately no GSAP or ScrollTrigger. Those total about 112KB and this site
 * publishes its own performance scores, so the effects here are written
 * natively instead. Lenis is the one dependency, self-hosted at about 13KB,
 * because inertial scroll is the single biggest contributor to perceived
 * smoothness and is genuinely hard to reproduce well by hand.
 *
 * Every effect is disabled under prefers-reduced-motion, and every one of them
 * is an enhancement: with JavaScript off, nothing here is required for the
 * page to be readable or operable.
 */
(function () {
  var reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var finePointer = window.matchMedia && window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  var root = document.documentElement;

  /* ----------------------------------------------------------------------
     1. Inertial smooth scroll.

     Lenis wraps native scrolling rather than transforming a container, so
     position: sticky, anchor links and the browser's own scrollbar all keep
     working. Driven by a plain rAF loop here, since there is no GSAP ticker
     to hang it off.
     ---------------------------------------------------------------------- */
  function initSmoothScroll() {
    if (reduceMotion || !window.Lenis) return null;
    /* Coarse pointers already have momentum scrolling from the OS. Layering
       Lenis on top of it fights the platform and feels worse, not better. */
    if (!finePointer) return null;

    var lenis = new window.Lenis({
      duration: 1.2,
      /* Exponential ease out: fast to start, long quiet settle. */
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      autoRaf: false
    });

    function raf(time) {
      lenis.raf(time);
      window.requestAnimationFrame(raf);
    }
    window.requestAnimationFrame(raf);

    root.classList.add("has-smooth-scroll");
    window.upstreamLenis = lenis;

    /* In-page anchors must route through Lenis, otherwise the browser jumps
       instantly and the eased scroll is bypassed. */
    document.addEventListener("click", function (event) {
      var link = event.target.closest && event.target.closest('a[href^="#"]');
      if (!link) return;
      var id = link.getAttribute("href");
      if (!id || id === "#") return;
      var target = document.querySelector(id);
      if (!target) return;
      event.preventDefault();
      lenis.scrollTo(target, { offset: -90 });
      /* Keep the keyboard in step with the eye. */
      target.setAttribute("tabindex", "-1");
      target.focus({ preventScroll: true });
    });

    return lenis;
  }

  /* ----------------------------------------------------------------------
     2. Reveal choreography.

     Two observers rather than one. A fixed threshold makes a small card and a
     full-height section reveal at the same late point, which leaves large
     sections snapping in after they are already past. Large elements are given
     an earlier trigger so they glide into their resting position while the
     reader is still scrolling toward them.
     ---------------------------------------------------------------------- */
  function initReveals(scope) {
    var host = scope || document;
    var selector = "[data-motion-reveal]:not([data-reveal-bound]), .motion-reveal:not([data-reveal-bound])";
    var targets = Array.prototype.slice.call(host.querySelectorAll(selector));
    if (!targets.length) return;

    if (reduceMotion || !("IntersectionObserver" in window)) {
      targets.forEach(function (el) {
        el.setAttribute("data-reveal-bound", "true");
        el.classList.add("motion-reveal", "is-visible");
      });
      return;
    }

    function revealOne(entries, observer) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var delay = parseInt(el.getAttribute("data-motion-delay") || "0", 10);
        if (delay > 0) {
          window.setTimeout(function () { el.classList.add("is-visible"); }, delay);
        } else {
          el.classList.add("is-visible");
        }
        observer.unobserve(el);
      });
    }

    var small = new IntersectionObserver(revealOne, { threshold: 0.12, rootMargin: "0px 0px -40px 0px" });
    var large = new IntersectionObserver(revealOne, { threshold: 0, rootMargin: "0px 0px 12% 0px" });
    var viewport = window.innerHeight || root.clientHeight || 800;

    /* Hiding a page behind an animation is the worst failure this system can
       produce, so it fails open in two ways.

       First: anything already at or above the fold when it binds is revealed
       straight away rather than waiting for an intersection that may never
       come. Second: a watchdog reveals anything still hidden a few seconds
       later, which covers a zero-height viewport, a detached container, or any
       browser quirk that stops the observer firing. */
    function revealNow(el) {
      el.classList.add("is-visible");
    }

    targets.forEach(function (el, index) {
      el.setAttribute("data-reveal-bound", "true");
      el.classList.add("motion-reveal", "is-observed");

      /* Stagger siblings inside a marked group so a grid arrives as a wave
         rather than all at once. Capped so a long list never crawls. */
      var group = el.closest("[data-motion-stagger]");
      if (group) {
        var siblings = Array.prototype.slice.call(group.querySelectorAll("[data-motion-reveal], .motion-reveal"));
        var position = Math.max(0, siblings.indexOf(el));
        el.style.setProperty("--motion-stagger-index", String(Math.min(position, 6)));
      } else {
        el.style.setProperty("--motion-stagger-index", String(Math.min(index, 3)));
      }

      var rect = el.getBoundingClientRect();

      /* Already on screen at bind time: show it now. Waiting would mean the
         first thing a visitor sees is blank. */
      if (rect.top < viewport * 0.9) {
        revealNow(el);
        return;
      }

      var height = rect.height || el.offsetHeight || 0;
      (height >= viewport * 0.6 ? large : small).observe(el);
    });

    /* Watchdog. If anything is still hidden after this, something stopped the
       observers and content matters more than choreography. */
    window.setTimeout(function () {
      targets.forEach(function (el) {
        if (!el.classList.contains("is-visible")) revealNow(el);
      });
    }, 2500);
  }

  /* ----------------------------------------------------------------------
     3. Magnetic controls.

     Buttons drift a few pixels toward the cursor. Delegated on document so it
     covers controls added later by the theme editor or a tool, and capped
     tightly: this should read as responsiveness, not as a toy.
     ---------------------------------------------------------------------- */
  function initMagnetic() {
    if (reduceMotion || !finePointer) return;

    var STRENGTH = 0.16;
    var CAP = 5;
    var SELECTOR = ".button, [data-magnetic]";

    function control(node) {
      if (!node || !node.closest) return null;
      return node.closest(SELECTOR);
    }

    document.addEventListener("mousemove", function (event) {
      var el = control(event.target);
      if (!el) return;
      var rect = el.getBoundingClientRect();
      var dx = (event.clientX - (rect.left + rect.width / 2)) * STRENGTH;
      var dy = (event.clientY - (rect.top + rect.height / 2)) * STRENGTH;
      var x = Math.max(-CAP, Math.min(CAP, dx));
      var y = Math.max(-CAP, Math.min(CAP, dy));
      el.style.transform = "translate3d(" + x + "px," + y + "px,0)";
    }, { passive: true });

    /* mouseout bubbles where mouseleave does not, so it can be delegated. */
    document.addEventListener("mouseout", function (event) {
      var el = control(event.target);
      if (el) el.style.transform = "";
    }, { passive: true });

    /* A keyboard user never fires mousemove, so clear any residual offset when
       focus moves on. */
    document.addEventListener("focusin", function (event) {
      var el = control(event.target);
      if (el) el.style.transform = "";
    });
  }

  /* ----------------------------------------------------------------------
     4. Section entry for ported pages.

     The ported page markup carries .section wrappers that predate the reveal
     system. Marking their inner content opts every page into the same
     choreography as the home page without editing 20 section files.
     ---------------------------------------------------------------------- */
  function markPortedSections(scope) {
    var host = scope || document;
    var blocks = host.querySelectorAll(".section .section-inner:not([data-motion-reveal]):not([data-reveal-bound])");
    Array.prototype.forEach.call(blocks, function (el) {
      /* Never hide a tool: its controls must be usable the moment it paints. */
      if (el.closest(".tool-shell") || el.querySelector("[data-tool]")) return;
      el.setAttribute("data-motion-reveal", "");
    });
  }

  function start(scope) {
    markPortedSections(scope);
    initReveals(scope);
  }

  function boot() {
    root.classList.add("motion-ready");
    initSmoothScroll();
    initMagnetic();
    start(document);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }

  /* Shopify theme editor: section markup is replaced wholesale on edit, so the
     new nodes are unbound. Re-run only the scoped, idempotent pieces. */
  document.addEventListener("shopify:section:load", function (event) {
    start(event.target);
  });
  document.addEventListener("shopify:section:reorder", function (event) {
    start(event.target);
  });

  window.UpstreamMotionPlus = {
    initReveals: initReveals,
    markPortedSections: markPortedSections
  };
}());
